import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";

import Sheet from 'components/core/Sheet';
import Card from 'components/core/Card';
import Typography from 'components/core/Typography';
import Autocomplete from 'components/core/Autocomplete';
import Stack from 'components/core/Stack';
import Grid from 'components/core/Grid';
import Select from 'components/core/Select';
import Input from 'components/core/Input';
import Button from 'components/core/Button';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import TaxonomyForm from 'components/TaxonomyForm';
import TaxonomyDossier from 'components/TaxonomyDossier';

import taxonomyNameActions from 'actions/taxonomyNameActions';
import taxonomyActions from 'actions/taxonomyActions';
import lookupTableActions from 'actions/lookupTableActions';

const TaxonomyAdd = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const me = useSelector(({ profile }) => profile.me);

    if (!me?.get('type') || me?.get('type') !== 'ADMIN') {
        navigate('/');
        return null;
    }

    const [newTaxonomy, setNewTaxonomy] = useState(false);
    const [taxonomy, setTaxonomy] = useState(null);
    const [name, setName] = useState({ typeId: 'common', name: '' });
    const [errors, setErrors] = useState({});

    const taxonomyNameSynonyms = useSelector(({ taxonomyName }) => taxonomyName.list);
    const options = useSelector(({ lookupTable }) => lookupTable.options);
    const taxonomies = useSelector(({ taxonomy }) => taxonomy.list);

    useEffect(() => {
        dispatch(taxonomyActions.list());
    }, [dispatch]);

    useEffect(() => {
        if (taxonomy) {
            dispatch(taxonomyNameActions.listSynonyms(taxonomy))
        }
    }, [taxonomy])
    
    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
    }, [dispatch]);

    const handleSubmit = async () => {
        setErrors({});
        if (!name.typeId) {
            setErrors({ ...errors, typeId: 'Type is required' });
            return;
        }
        if (!name.name) {
            setErrors({ ...errors, name: 'Name is required' });
            return;
        }

        const nameData = {
            typeId: name.typeId,
            name: name.name,
            taxonomy: taxonomy.id
        };
        await dispatch(taxonomyNameActions.save(nameData));
        navigate('/');
    };

    const handleSelectTaxonomy = (taxonomy) => {
        setTaxonomy(taxonomy);
    }

    const noOptionComponent = () => {
        return (
            <li onClick={() => { setNewTaxonomy(true) }} style={{cursor: 'pointer'}}>
                <Stack direction="row">
                    <AddCircleOutlineIcon sx={{marginRight: 2, color: 'black'}}/>
                    <Typography>Add New Taxonomy</Typography>
                </Stack>
            </li>
        )
    }

    const handleBack = useCallback((e) => {
        window.history.back();
    }, [navigate]);

    const renderNameAddition = () => {
        if (!taxonomy) {
            return null;
        }

        const alreadyHasName = taxonomyNameSynonyms.map((s) => s.name).includes(name.name);

        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TaxonomyDossier 
                        taxonomy={taxonomy}
                        taxonomyNameSynonyms={taxonomyNameSynonyms}
                    />
                </Grid>
                <Grid item xs={3}>
                    <Select
                        label="Type"
                        value={name.typeId}
                        onChange={(value) => {
                            setName({ ...name, typeId: value });
                        }}
                        options={['scientific', 'common']}
                        labels={{
                            scientific: 'Scientific',
                            common: 'Common'
                        }}
                        error={errors.typeId}
                        required
                    />
                </Grid>
                <Grid item xs={9}>
                    <Input
                        label="Name"
                        value={name.name}
                        onChange={(value) => {
                            setName({ ...name, name: value });
                        }}
                        error={errors.name}
                        required
                    />
                </Grid>
                <Grid item xs={12}>
                    <Button onClick={handleSubmit} disabled={alreadyHasName}>
                        Create Taxonomy
                    </Button>
                </Grid>
            </Grid>
        )
    }

    return (
        <Sheet sx={{ p: 4 }}>
            <Button
                variant="text" 
                onClick={handleBack}
                sx={{ padding: 2}}
            >
                <ArrowBackIosIcon sx={{ fontSize: 18, marginRight: 1 }} />
                Back
            </Button>
            <Card>
                {newTaxonomy ? (
                    <>
                        <Typography variant="h4" sx={{ mb: 4 }}>Add New Taxonomy</Typography>
                        <TaxonomyForm 
                            taxonomies={taxonomies}
                            options={options}
                            onSubmit={() => {navigate('/');}}
                        />
                    </>
                ) : (
                    <Stack>
                        <Typography variant="h4" sx={{ mb: 4 }}>Add A New Taxonomy Name</Typography>
                        <Autocomplete
                            placeholder="Select Taxonomy"
                            onChange={handleSelectTaxonomy}
                            options={taxonomies}
                            noOptionComponent={noOptionComponent()}
                            getOptionLabel={option => option.primaryName}
                            noOptionAlwaysVisible
                            sx={{ mb: 2, width: 'auto' }}
                        />
                        {renderNameAddition()}
                    </Stack>
                )}
            </Card>
        </Sheet>
    );
};

export default TaxonomyAdd;